// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const ARCHIVED_PROJECTS = [
  { id:1, name:"CropPatrol Initial Deployment", completedDate:"2026-01-15", duration:"8 weeks", lead:"Tyson Becker", status:"Completed", product:"CropPatrol",
    summary:"Successfully deployed CropPatrol to first 5 agricultural clients. Platform handles grain storage monitoring, crop tracking, and equipment management.",
    metrics:{ tasksCompleted:34, onTime:true, budget:"Under by 8%", satisfaction:"4.5/5" },
    lessons:["Start beta testing earlier to catch edge cases","Grain bin sensor calibration needs site-specific adjustments","Rural internet connectivity requires offline mode"] },
  { id:2, name:"Vantage Ops Delivery Permits", completedDate:"2026-01-20", duration:"4 weeks", lead:"Jordan Lutz", status:"Completed", product:"Vantage Ops",
    summary:"Added BC and Alberta oversized load delivery permit management with 6 tabs covering permit categories, dimension checking, and route planning.",
    metrics:{ tasksCompleted:18, onTime:true, budget:"On budget", satisfaction:"4.8/5" },
    lessons:["Provincial regulations differ significantly - build modular compliance","George Massey Tunnel height restriction was a critical requirement","Auto-analysis feature was the most valued addition"] },
  { id:3, name:"Directional Ops Recording Pages", completedDate:"2026-01-25", duration:"3 weeks", lead:"Jordan Lutz", status:"Completed", product:"Directional Ops",
    summary:"Built and deployed site recording, e-signature consent, and transcription pages for directional drilling operations.",
    metrics:{ tasksCompleted:12, onTime:true, budget:"On budget", satisfaction:"4.2/5" },
    lessons:["Canvas-based signature pad must support both mouse and touch events","E-signature must be completed BEFORE recording begins","Transcription accuracy improves with industry-specific vocabulary"] },
  { id:4, name:"OreSync Recording Pages", completedDate:"2026-01-28", duration:"3 weeks", lead:"Jordan Lutz", status:"Completed", product:"OreSync",
    summary:"Added site recording and transcription capabilities to OreSync for mine site walkthroughs and safety inspections.",
    metrics:{ tasksCompleted:11, onTime:true, budget:"Under by 5%", satisfaction:"4.3/5" },
    lessons:["Mining environments need rugged device considerations","Audio quality in noisy environments needs noise cancellation","Safety compliance recordings have specific retention requirements"] },
  { id:5, name:"ClosingDesk 3D/360 Features", completedDate:"2026-01-30", duration:"5 weeks", lead:"Jordan Lutz", status:"Completed", product:"ClosingDesk",
    summary:"Added 3D floor plans, 360-degree virtual tours, Matterport integration, drone aerial views, AR staging, and property media gallery to ClosingDesk.",
    metrics:{ tasksCompleted:28, onTime:false, budget:"Over by 12%", satisfaction:"4.7/5" },
    lessons:["3D rendering is computationally expensive - need lazy loading","Matterport API has rate limits that need caching layer","AR staging feature was scope creep but highly valued by agents"] },
  { id:6, name:"Pitch Deck Suite", completedDate:"2026-01-10", duration:"6 weeks", lead:"Ryan Miskulin", status:"Completed", product:"All Products",
    summary:"Created 15 professional pitch decks for all Forge products plus a custom Canstorr-specific CropPatrol pitch deck.",
    metrics:{ tasksCompleted:16, onTime:true, budget:"On budget", satisfaction:"4.6/5" },
    lessons:["Custom pitch decks for specific prospects convert significantly better","Keep CTAs pointing to sales team, not prospects","Include real metrics and case studies when available"] },
  { id:7, name:"PolicyForge Scaffolding", completedDate:"2026-01-18", duration:"2 weeks", lead:"Jordan Lutz", status:"Completed", product:"PolicyForge",
    summary:"Set up initial scaffolding with 12 core pages including Login, Dashboard, ClientBook, Leads, Renewals, and more.",
    metrics:{ tasksCompleted:14, onTime:true, budget:"On budget", satisfaction:"4.0/5" },
    lessons:["Insurance terminology is domain-specific - need SME input","Policy comparison feature requires standardized data format","Compliance requirements vary by province"] },
  { id:8, name:"BudLedger Compliance Update", completedDate:"2025-12-20", duration:"4 weeks", lead:"Jordan Lutz", status:"Completed", product:"BudLedger",
    summary:"Updated BudLedger compliance tracking to meet 2026 regulatory changes across Canadian provinces.",
    metrics:{ tasksCompleted:22, onTime:true, budget:"On budget", satisfaction:"4.4/5" },
    lessons:["Cannabis regulations change frequently - build flexible rule engine","Province-by-province compliance is essential","Audit trail feature is critical for license renewals"] },
  { id:9, name:"Mobile App Framework Setup", completedDate:"2025-12-15", duration:"3 weeks", lead:"Jordan Lutz", status:"Completed", product:"Mobile Platform",
    summary:"Established React Native / Expo SDK 50 framework for all 18 mobile companion apps with shared authentication and component library.",
    metrics:{ tasksCompleted:20, onTime:true, budget:"Under by 10%", satisfaction:"4.1/5" },
    lessons:["Shared component library saves significant development time","Expo SDK 50 has improved build times over SDK 49","Push notification setup should be done early in the process"] },
  { id:10, name:"Domain Research and Planning", completedDate:"2025-12-28", duration:"2 weeks", lead:"Ryan Miskulin", status:"Cancelled", product:"Infrastructure",
    summary:"Research for 14 .com domains for the product suite. Identified naming conflicts and alternatives. Project paused pending budget approval.",
    metrics:{ tasksCompleted:8, onTime:false, budget:"N/A", satisfaction:"N/A" },
    lessons:["Domain availability changes quickly - register immediately when decided","Some chosen names had trademark conflicts","Budget for domains should be approved before research begins"] }
];

const STATUSES = ["All","Completed","Cancelled","On Hold"];

export default function ProjectArchivePage() {
  const [projects] = useState(ARCHIVED_PROJECTS);
  const [selectedProject, setSelectedProject] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  const filtered = projects.filter(p => {
    if (statusFilter !== "All" && p.status !== statusFilter) return false;
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase()) && !p.product.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const statusBadge = (status) => {
    const map = { Completed:"badge badge-green", Cancelled:"badge badge-red", "On Hold":"badge badge-yellow" };
    return map[status] || "badge";
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Project Archive</h1><p>Completed and archived projects</p></div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Archived</div><div className="stat-value">{projects.length}</div></div>
        <div className="stat-card"><div className="stat-label">Completed</div><div className="stat-value" style={{color:"#10b981"}}>{projects.filter(p=>p.status==="Completed").length}</div></div>
        <div className="stat-card"><div className="stat-label">Cancelled</div><div className="stat-value" style={{color:"#ef4444"}}>{projects.filter(p=>p.status==="Cancelled").length}</div></div>
        <div className="stat-card"><div className="stat-label">Avg Satisfaction</div><div className="stat-value" style={{color:"#10b981"}}>4.4/5</div></div>
      </div>

      <div style={{display:"flex",gap:12,marginBottom:16}}>
        <input className="input" placeholder="Search archived projects..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{flex:1}} />
        <select className="input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{width:180}}>
          {STATUSES.map(s => <option key={s} value={s}>{s === "All" ? "All Statuses" : s}</option>)}
        </select>
      </div>

      <div className="card">
        <div className="card-header"><h3>Archived Projects</h3><span style={{fontSize:12,color:"#64748b"}}>{filtered.length} projects</span></div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{borderBottom:"2px solid #334155"}}>
              {["Project","Product","Completed","Duration","Lead","Status"].map(h => (
                <th key={h} style={{textAlign:"left",padding:"10px 8px",fontSize:12,fontWeight:700,color:"#94a3b8"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} style={{borderBottom:"1px solid #334155",cursor:"pointer"}} onClick={() => setSelectedProject(p)}>
                <td style={{padding:"10px 8px",fontSize:13,fontWeight:600,color:"#e2e8f0"}}>{p.name}</td>
                <td style={{padding:"10px 8px",fontSize:12,color:"#94a3b8"}}>{p.product}</td>
                <td style={{padding:"10px 8px",fontSize:12,color:"#94a3b8"}}>{p.completedDate}</td>
                <td style={{padding:"10px 8px",fontSize:12,color:"#94a3b8"}}>{p.duration}</td>
                <td style={{padding:"10px 8px",fontSize:12,color:"#e2e8f0"}}>{p.lead}</td>
                <td style={{padding:"10px 8px"}}><span className={statusBadge(p.status)}>{p.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedProject && (
        <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedProject.name}</h2>
              <button className="modal-close" onClick={() => setSelectedProject(null)}>X</button>
            </div>
            <div className="modal-body">
              <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                <span className={statusBadge(selectedProject.status)}>{selectedProject.status}</span>
                <span style={{fontSize:13,color:"#94a3b8"}}>Product: <strong style={{color:"#e2e8f0"}}>{selectedProject.product}</strong></span>
                <span style={{fontSize:13,color:"#94a3b8"}}>Lead: <strong style={{color:"#e2e8f0"}}>{selectedProject.lead}</strong></span>
                <span style={{fontSize:13,color:"#94a3b8"}}>Duration: <strong style={{color:"#e2e8f0"}}>{selectedProject.duration}</strong></span>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>Summary</h4>
                <p style={{fontSize:13,color:"#e2e8f0",lineHeight:1.6}}>{selectedProject.summary}</p>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:8}}>Metrics</h4>
                <div className="grid-2" style={{gap:8}}>
                  <div style={{padding:8,background:"#0f172a",borderRadius:6,fontSize:12}}><span style={{color:"#64748b"}}>Tasks Completed: </span><span style={{color:"#e2e8f0",fontWeight:600}}>{selectedProject.metrics.tasksCompleted}</span></div>
                  <div style={{padding:8,background:"#0f172a",borderRadius:6,fontSize:12}}><span style={{color:"#64748b"}}>On Time: </span><span style={{color:selectedProject.metrics.onTime?"#10b981":"#ef4444",fontWeight:600}}>{selectedProject.metrics.onTime?"Yes":"No"}</span></div>
                  <div style={{padding:8,background:"#0f172a",borderRadius:6,fontSize:12}}><span style={{color:"#64748b"}}>Budget: </span><span style={{color:"#e2e8f0",fontWeight:600}}>{selectedProject.metrics.budget}</span></div>
                  <div style={{padding:8,background:"#0f172a",borderRadius:6,fontSize:12}}><span style={{color:"#64748b"}}>Satisfaction: </span><span style={{color:"#e2e8f0",fontWeight:600}}>{selectedProject.metrics.satisfaction}</span></div>
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:8}}>Lessons Learned</h4>
                {selectedProject.lessons.map((l, i) => (
                  <div key={i} style={{display:"flex",gap:8,padding:"6px 0",borderBottom:"1px solid #334155"}}>
                    <span style={{color:"#eab308",fontSize:12}}>{i + 1}.</span>
                    <span style={{fontSize:13,color:"#e2e8f0"}}>{l}</span>
                  </div>
                ))}
              </div>

              <div style={{display:"flex",gap:8}}>
                <button className="btn btn-outline">Reactivate Project</button>
                <button className="btn btn-outline">Export Report</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
